import { createAsyncThunk } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { genericFetcher } from '@gjensidige/service-async';
import { GjeFetchError } from '@gjensidige/service-async/lib/types/errors/GjeFetchError';
import { remoteLogFetchError } from '@gjensidige/service-error-handler';

import {
  AsyncChatConfig,
  ChatQueue,
  DefaultOpeningHours,
  SubTopic,
  Topic
} from '../../types/async-config';
import { boolify } from '../../utils/type-conversion';
import {
  selectDirectEntrance,
  setCallMeRouting,
  setServiceQueuesShortNames,
  setServiceQueuesShortNamesAndEntrances,
  setShortNames,
  setShortNamesAndEntrances,
  setShowDigitalContactLink,
  ShortNameAndEntrance
} from '../slices/config';
import { initializeTopicSelection } from '../slices/topic-selection';
import { AppThunk, RootState } from '../store';

/**
 * This will get all shortNames from an array of queues recursively.
 *
 * @param queues
 */
function getShortNames(queues: (ChatQueue | Topic | SubTopic)[]): string[] {
  return queues.flatMap((it) => {
    const shortNames: string[] = [];
    if ('topics' in it && it.topics) {
      shortNames.push(...getShortNames(it.topics));
    }
    if ('subTopics' in it && it.subTopics) {
      shortNames.push(...getShortNames(it.subTopics));
    }
    if (it.shortName) {
      shortNames.push(it.shortName);
    }
    return shortNames;
  });
}

function getShortNamesAndEntrance(
  queues: (ChatQueue | Topic | SubTopic)[]
): ShortNameAndEntrance[] {
  return queues.flatMap((it) => {
    const shortNames: ShortNameAndEntrance[] = [];
    if ('topics' in it && it.topics) {
      shortNames.push(...getShortNamesAndEntrance(it.topics));
    }
    if ('subTopics' in it && it.subTopics) {
      shortNames.push(...getShortNamesAndEntrance(it.subTopics));
    }
    if (it.shortName) {
      shortNames.push({ shortName: it.shortName, entrance: it.entrance });
    }
    return shortNames;
  });
}

const getDefaultOpeningHours = (
  intervals: DefaultOpeningHours
): DefaultOpeningHours => {
  return intervals.map((item) => {
    if (item) {
      return {
        open: dayjs()
          .set('hour', Number(item.open))
          .startOf('hour')
          .toISOString(),
        close: dayjs()
          .set('hour', Number(item.close))
          .startOf('hour')
          .toISOString()
      };
    }

    return null;
  });
};

const parseDirectEntrance =
  (asyncConfig: AsyncChatConfig): AppThunk =>
  (dispatch, getState) => {
    const { directEntrance, directErrand } = getState().config;
    if (directEntrance && asyncConfig.directEntrances) {
      for (const entranceConfig of asyncConfig.directEntrances) {
        if (
          entranceConfig.entrance === directEntrance &&
          (!directErrand || entranceConfig.errand === directErrand)
        ) {
          dispatch(selectDirectEntrance(entranceConfig));
          dispatch(
            initializeTopicSelection({
              selectedErrand: entranceConfig.errand,
              selectedEntrance: entranceConfig.entrance,
              authenticate: entranceConfig.authenticate,
              popupAuthentication: boolify(entranceConfig.popupAuthentication)
            })
          );
          break;
        }
      }
    }
  };

export const requestConfig = createAsyncThunk<
  AsyncChatConfig,
  string,
  { rejectValue: GjeFetchError; state: RootState }
>('config/request', async (url, { rejectWithValue, dispatch }) => {
  const res = await genericFetcher.getJson<AsyncChatConfig>(url, {
    clientIdentificationHeaders: url.startsWith('/')
  });

  if (res.isOk()) {
    dispatch(setShortNames(getShortNames(res.value.chatQueues)));
    dispatch(
      setShortNamesAndEntrances(getShortNamesAndEntrance(res.value.chatQueues))
    );
    dispatch(
      setShowDigitalContactLink(res.value.showDigitalContactLink || false)
    );

    if (res.value.serviceQueues) {
      dispatch(
        setServiceQueuesShortNames(getShortNames(res.value.serviceQueues))
      );
      dispatch(
        setServiceQueuesShortNamesAndEntrances(
          getShortNamesAndEntrance(res.value.serviceQueues)
        )
      );
    }

    dispatch(parseDirectEntrance(res.value));

    if (res.value.callMeRouting) {
      dispatch(setCallMeRouting(res.value.callMeRouting));
    }

    if (res.value.defaultOpeningHours) {
      return {
        ...res.value,
        defaultOpeningHours: getDefaultOpeningHours(
          res.value.defaultOpeningHours
        )
      };
    }

    return res.value;
  }

  void remoteLogFetchError(url, res.error);
  return rejectWithValue(res.error);
});
